import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Clock,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Calendar,
  Loader2,
  Play,
  RotateCcw
} from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SchedulerStatus = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchStatus();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/scheduler/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error("Error fetching scheduler status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessNow = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/scheduler/process`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { published, failed, processed } = response.data;
      
      if (processed === 0) {
        toast.info("No hay posts pendientes de publicar");
      } else if (failed === 0) {
        toast.success(`${published} posts publicados correctamente`);
      } else {
        toast.warning(`${published} publicados, ${failed} fallidos`);
      }
      
      fetchStatus();
    } catch (error) {
      toast.error("Error al procesar posts");
    } finally {
      setProcessing(false);
    }
  };

  const handleRetry = async (postId) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/scheduler/retry/${postId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === "published") {
        toast.success("Post publicado correctamente");
      } else {
        toast.warning("Publicación parcial");
      }
      
      fetchStatus();
    } catch (error) {
      toast.error("Error al reintentar publicación");
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return format(date, "d MMM, HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="scheduler-status">
      {/* Status Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">Publicación Automática</CardTitle>
                <CardDescription>
                  Los posts programados se publican automáticamente
                </CardDescription>
              </div>
            </div>
            <Badge variant={status?.scheduler_active ? "default" : "secondary"} className="gap-1">
              {status?.scheduler_active ? (
                <>
                  <CheckCircle className="w-3 h-3" />
                  Activo
                </>
              ) : (
                <>
                  <AlertTriangle className="w-3 h-3" />
                  Inactivo
                </>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{status?.pending_count || 0}</span> posts pendientes
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleProcessNow}
              disabled={processing}
              data-testid="process-now-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Procesar Ahora
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Overdue Posts */}
      {status?.overdue?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <CardTitle className="text-base text-amber-800">
                Posts Atrasados ({status.overdue.length})
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {status.overdue.map((post) => (
              <div 
                key={post.post_id}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{post.content_preview}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-muted-foreground">
                      Programado: {formatDate(post.scheduled_at)}
                    </span>
                    <div className="flex gap-1">
                      {post.platforms?.slice(0, 3).map((p) => (
                        <Badge key={p} variant="outline" className="text-[10px] capitalize">
                          {p}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRetry(post.post_id)}
                  className="text-amber-700 hover:text-amber-800 hover:bg-amber-100"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Reintentar
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Upcoming Posts */}
      {status?.upcoming?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              <CardTitle className="text-base">
                Próximos Posts ({status.upcoming.length})
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {status.upcoming.slice(0, 5).map((post) => (
              <div 
                key={post.post_id}
                className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{post.content_preview}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-muted-foreground">
                      {formatDate(post.scheduled_at)}
                    </span>
                    <div className="flex gap-1">
                      {post.platforms?.slice(0, 3).map((p) => (
                        <Badge key={p} variant="outline" className="text-[10px] capitalize">
                          {p}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* No pending posts */}
      {status?.pending_count === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No tienes posts programados</p>
          <p className="text-sm">Crea un post y prográmalo para publicar automáticamente</p>
        </div>
      )}
    </div>
  );
};
